<template>
  <div class="user-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <el-row :gutter="20">
          <el-col :span="16">
            <div style="display: flex;">
              <el-input
                v-model="searchUsername"
                placeholder="搜索用户名"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handleSearch"
                style="width: 240px; margin-right: 10px;"
              ></el-input>
              <el-button
                type="primary"
                icon="el-icon-search"
                @click="handleSearch"
              >搜索</el-button>
            </div>
          </el-col>
          <el-col :span="8">
            <el-button
              type="primary"
              icon="el-icon-refresh"
              @click="refreshList"
            >刷新列表</el-button>
          </el-col>
        </el-row>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        description="暂无用户数据"
      />

      <div v-else>
        <!-- 用户表格 -->
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载用户数据..."
          class="animate__animated animate__fadeIn"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" min-width="150" show-overflow-tooltip />
          <el-table-column label="余额" width="120" align="center">
            <template slot-scope="scope">
              <div v-if="scope.row.balanceLoading">查询中...</div>
              <div v-else-if="scope.row.totalRevenue !== undefined">
                {{ scope.row.totalRevenue }}
                <el-button
                  size="mini"
                  type="text"
                  @click="hideBalance(scope.row)"
                  style="margin-left: 10px; color: #909399;"
                >(隐藏)</el-button>
              </div>
              <el-button
                v-else
                size="mini"
                type="text"
                @click="checkBalance(scope.row)"
              >查询余额</el-button>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="创建时间" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column prop="updatedAt" label="更新时间" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.updatedAt) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="250" align="center">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="warning"
                icon="el-icon-key"
                @click="handleResetPassword(scope.row)"
                v-if="isRolePermitted"
              >重置密码</el-button>
              <el-button
                size="mini"
                type="danger"
                icon="el-icon-switch-button"
                @click="handleDeactivateAccount(scope.row)"
                v-if="isRolePermitted"
              >注销账户</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :page-sizes="[5, 10, 20, 50]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 重置密码确认对话框 -->
    <el-dialog
      title="重置用户密码"
      :visible.sync="resetPasswordDialogVisible"
      width="400px"
      center
    >
      <p>确定要重置用户 <strong>{{ currentUser.username }}</strong> 的密码吗？</p>
      <p>重置后密码将恢复为默认密码。</p>
      <div slot="footer" class="dialog-footer">
        <el-button @click="resetPasswordDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmResetPassword">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 注销账户确认对话框 -->
    <el-dialog
      title="注销用户账户"
      :visible.sync="deactivateDialogVisible"
      width="400px"
      center
    >
      <p>警告：此操作将永久注销用户 <strong>{{ currentUser.username }}</strong> 的账户！</p>
      <p>该操作不可撤销，所有相关数据也将被删除。</p>
      <p>请确认是否继续执行此操作？</p>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deactivateDialogVisible = false">取 消</el-button>
        <el-button type="danger" @click="confirmDeactivateAccount">确 定注销</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { request } from '@/utils/http'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      error: '',
      searchUsername: '',
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      resetPasswordDialogVisible: false,
      deactivateDialogVisible: false,
      currentUser: {}
    }
  },
  computed: {
    isRolePermitted() {
      // 获取本地存储的角色，默认为0（即允许访问）
      const role = localStorage.getItem('role');
      return role === '0';
    }
  },
  async mounted() {
    await this.fetchUsers()
  },
  methods: {
    async fetchUsers() {
      this.loading = true
      this.error = ''
      
      try {
        // 构建查询参数
        let params = new URLSearchParams();
        params.append('pageNum', this.pagination.pageNum);
        params.append('pageSize', this.pagination.pageSize);
        
        // 添加用户名筛选参数
        if (this.searchUsername) {
          params.append('username', this.searchUsername);
        }
        
        const queryString = params.toString();
        const response = await request(`/admin/users/page?${queryString}`)
        // 初始化balanceLoading和totalRevenue字段
        this.users = response.data.records.map(user => ({
          ...user,
          balanceLoading: false,
          totalRevenue: undefined
        }))
        this.pagination.total = response.data.total
        
      } catch (error) {
        console.error('获取用户列表失败:', error)
        this.error = error.message || '获取用户列表失败'
      } finally {
        this.loading = false
      }
    },
    
    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum
      this.fetchUsers()
    },
    
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize
      this.pagination.pageNum = 1
      this.fetchUsers()
    },
    
    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchUsers()
    },
    
    refreshList() {
      this.searchUsername = ''
      this.pagination.pageNum = 1
      this.fetchUsers()
    },
    
    handleResetPassword(user) {
      this.currentUser = user
      this.resetPasswordDialogVisible = true
    },
    
    handleDeactivateAccount(user) {
      this.currentUser = user
      this.deactivateDialogVisible = true
    },
    
    async confirmResetPassword() {
      try {
        const response = await request(`/admin/users/${this.currentUser.id}/reset-password`, {
          method: 'PUT'
        })
        
        this.$message.success(response.data)
        this.resetPasswordDialogVisible = false
      } catch (error) {
        this.$message.error(error.message || '重置密码失败')
      }
    },
    
    async confirmDeactivateAccount() {
      try {
        await request(`/admin/users/${this.currentUser.id}`, {
          method: 'DELETE'
        })
        
        this.$message.success(`用户 ${this.currentUser.username} 的账户已注销`)
        this.deactivateDialogVisible = false
        this.fetchUsers() // 刷新列表
      } catch (error) {
        this.$message.error(error.message || '注销账户失败')
      }
    },
    
    async checkBalance(row) {
      // 设置加载状态
      this.$set(row, 'balanceLoading', true);
      
      try {
        const response = await request(`/admin/user-details/by-username?username=${encodeURIComponent(row.username)}`);
        // 更新用户余额信息
        this.$set(row, 'totalRevenue', response.data.totalRevenue);
      } catch (error) {
        console.error('查询用户余额失败:', error);
        this.$message.error(error.message || '查询用户余额失败');
      } finally {
        this.$set(row, 'balanceLoading', false);
      }
    },
    
    hideBalance(row) {
      this.$set(row, 'totalRevenue', undefined);
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.filter-card {
  margin-bottom: 20px;
}

.error {
  color: #f56c6c;
  padding: 10px;
  background-color: #fef0f0;
  border-radius: 4px;
  margin-bottom: 20px;
}

.el-table {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: center;
}

.dialog-footer {
  text-align: right;
}
</style>
